import { MutableRefObject } from 'react';

import { GridApi, GridGroupNode, GridPinnedColumnPosition } from '@mui/x-data-grid-premium';

import { SYSTEM_COLUMN_FIELDS } from '../GeneratikTable.constants';
import { GeneratikTableColumn } from '../GeneratikTable.types';
import { FilterFieldConfig, FilterValues } from '../GeneratikTableFilterPanel.types';
import { SortOption } from '../GeneratikTableToolbar.types';
import buildFilterValue from './buildFilterValue';

type ApiRef = MutableRefObject<GridApi>;
export type PinSide = 'left' | 'right';

export const hideColumn = (apiRef: ApiRef, field: string): void => {
  apiRef.current.setColumnVisibility(field, false);
};

export const togglePinColumn = (apiRef: ApiRef, field: string, side: PinSide): void => {
  const position = side as GridPinnedColumnPosition;
  if (apiRef.current.isColumnPinned(field) === position) {
    apiRef.current.unpinColumn(field);
  } else {
    apiRef.current.pinColumn(field, position);
  }
};

export const unpinColumn = (apiRef: ApiRef, field: string): void => {
  apiRef.current.unpinColumn(field);
};

export interface GroupDeps {
  onGroupChange?: (field: string | null) => void;
}

export const applyGroup = (field: string, deps: GroupDeps): void => {
  deps.onGroupChange?.(field);
};

// getAllRowIds() only returns leaf data rows (gridDataRowIdsSelector); the
// auto-generated tree-data group nodes are in the sorted row set.
const setAllGroupsExpansion = (apiRef: ApiRef, expanded: boolean): void => {
  apiRef.current.getSortedRowIds().forEach((id) => {
    const node = apiRef.current.getRowNode(id);
    if (node?.type === 'group') {
      apiRef.current.setRowChildrenExpansion(id, expanded);
    }
  });
};

export const collapseAllGroups = (apiRef: ApiRef): void => setAllGroupsExpansion(apiRef, false);

export const expandAllGroups = (apiRef: ApiRef): void => setAllGroupsExpansion(apiRef, true);

export const hasExpandedGroups = (apiRef: ApiRef): boolean =>
  apiRef.current.getSortedRowIds().some((id) => {
    const node = apiRef.current.getRowNode(id);
    return node?.type === 'group' && (node as GridGroupNode).childrenExpanded === true;
  });

export interface ResetWidthsDeps {
  apiRef: ApiRef;
  widthKey: string | null;
  clearColumnWidths: (key: string) => void;
  autosizeOptions?: object;
}

export const resetColumnWidths = ({
  apiRef,
  widthKey,
  clearColumnWidths,
  autosizeOptions,
}: ResetWidthsDeps): void => {
  if (widthKey) {
    // Persisted tables: clearing the snapshot re-hydrates the authored elastic
    // column defs and re-arms useAutosizeColumnsOnMount (its hasPersistedWidths
    // guard flips), which reproduces the fresh-visit layout and self-persists
    // it. Autosizing here as well would race that re-hydration and measure the
    // stale layout — with different options than the mount pass, so "reset"
    // landed on widths the user never saw as defaults.
    clearColumnWidths(widthKey);
    return;
  }
  // Tables without persistence never re-run the mount autosize on their own,
  // so reset re-measures directly — with the same options as the mount pass.
  void apiRef.current.autosizeColumns({
    includeHeaders: true,
    includeOutliers: false,
    expand: true,
    ...autosizeOptions,
  });
};

export interface FilterByElementDeps {
  filterFields: FilterFieldConfig[];
  filterValues: FilterValues;
  columnFilterMap: Record<string, string>;
  onFilterValuesChange?: (values: FilterValues) => void;
}

export const resolveFilterField = (
  field: string,
  columnFilterMap: Record<string, string>,
  filterFields: FilterFieldConfig[],
): FilterFieldConfig | undefined => {
  const filterId = columnFilterMap[field];
  if (!filterId) return undefined;
  return filterFields.find((f) => f.id === filterId);
};

export const filterByElement = (
  field: string,
  rawValue: unknown,
  deps: FilterByElementDeps,
): void => {
  const filterField = resolveFilterField(field, deps.columnFilterMap, deps.filterFields);
  if (!filterField) return;
  const value = buildFilterValue(filterField, rawValue);
  if (value === undefined) return;
  deps.onFilterValuesChange?.({ ...deps.filterValues, [filterField.id]: value });
};

export const deriveSortOptions = (columns: GeneratikTableColumn[]): SortOption[] =>
  columns
    .filter((c) => c.sortable !== false && !SYSTEM_COLUMN_FIELDS.has(c.field))
    .map((c) => ({ field: c.field, label: (c.headerName as string) || c.field }));
